/**
 * Tenant ID check — the client contract (docs/TenantIdVerification.md §2–§6, §8).
 *
 * The client reads the state from ONE place: `supabase.rpc('get_my_identity_status')` →
 * `IdentityStatus` (one row, or none while the users row does not exist yet). The nine states
 * (+ name_changed) are derived on the server (flat.identity_status, migration 20261030143708);
 * the client never derives them from identity_check_sessions rows.
 *
 * Starting / resuming a check: `supabase.functions.invoke('identity-create-session',
 * { body: { returnPath } })` → `{ url, launch, resumed }` or `{ error: 'already_verified' |
 * 'limit_reached' | 'in_review' | 'provider_unavailable' }` (409 / 429 / 409 / 503).
 * Polling while open: `identity-check-status` → `{ status }` (a session status, below).
 */

/** The pill on the profile row (Polish labels live in i18n, keyed by this value). */
export const IDENTITY_STATES = [
  'not_started',     // Nie rozpoczęta
  'started',         // Rozpoczęta — Dokończ (same session) · Zacznij od nowa
  'in_review',       // Sprawdzamy — our team decides in the provider console
  'resubmit',        // Do poprawy — a reviewer asked to redo `resubmit_features`
  'interrupted',     // Przerwana — Zacznij od nowa
  'declined',        // Nieudana — see `reason`
  'limit',           // Brak prób — Napisz do nas (3 counted sessions per rolling 30 days)
  'verified',        // Zweryfikowana
  'document_expired', // Dokument wygasł — Zweryfikuj ponownie
  'name_changed',    // Do weryfikacji — the declared name no longer matches the verified one
] as const;
export type IdentityState = (typeof IDENTITY_STATES)[number];

/** Why a check failed (docs §3). Only set while `state === 'declined'`. */
export const IDENTITY_DECLINE_REASONS = ['photo', 'face', 'expired_document', 'name_mismatch', 'copy', 'underage', 'other'] as const;
export type IdentityDeclineReason = (typeof IDENTITY_DECLINE_REASONS)[number];

/** identity_check_sessions.status — what `identity-check-status` returns while polling. */
export const IDENTITY_SESSION_STATUSES = [
  'pending', 'in_progress', 'in_review', 'resubmit', 'approved', 'declined', 'interrupted', 'document_expired',
] as const;
export type IdentitySessionStatus = (typeof IDENTITY_SESSION_STATUSES)[number];

/** Provider feature names a reviewer may ask to redo (Didit: OCR, LIVENESS, FACE_MATCH, …). */
export type IdentityResubmitFeature = string;

/** One row of public.get_my_identity_status(). */
export interface IdentityStatus {
  state: IdentityState;
  reason: IdentityDeclineReason | null;
  resubmit_features: IdentityResubmitFeature[];
  /** A new session may be created (not_started / interrupted / declined / document_expired / name_changed). */
  can_start: boolean;
  /** The open session can be reopened with „Dokończ” / „Popraw” (started / resubmit). */
  can_resume: boolean;
  /** The latest identity_check_sessions row, if any. */
  session_id: string | null;
  provider: string | null;
  session_status: IdentitySessionStatus | null;
  session_created_at: string | null;
  session_updated_at: string | null;
  /** The verified identity (docs §6/§8): present after a pass, also when `state` is name_changed. */
  verified_first_name: string | null;
  verified_last_name: string | null;
  /** YYYY-MM-DD — also copied into tenant_profiles.date_of_birth, which is then locked. */
  verified_date_of_birth: string | null;
  verified_issuing_country: string | null;
  verified_document_type: string | null;
  verified_at: string | null;
  /** Declared (users.first_name/last_name) vs verified name; null without a verified identity. */
  name_matches: boolean | null;
  /** When the 30-day cap lifts (ISO timestamp); only while `state === 'limit'`. The UI decides whether to show it. */
  limit_until: string | null;
}

export function isIdentityState(value: unknown): value is IdentityState {
  return typeof value === 'string' && (IDENTITY_STATES as readonly string[]).includes(value);
}

export function isIdentityDeclineReason(value: unknown): value is IdentityDeclineReason {
  return typeof value === 'string' && (IDENTITY_DECLINE_REASONS as readonly string[]).includes(value);
}

/** Narrows an RPC row — unknown values are dropped, never trusted. */
export function parseIdentityStatus(raw: unknown): IdentityStatus | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (!isIdentityState(r.state)) return null;
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  return {
    state: r.state,
    reason: isIdentityDeclineReason(r.reason) ? r.reason : null,
    resubmit_features: Array.isArray(r.resubmit_features) ? r.resubmit_features.filter((f): f is string => typeof f === 'string') : [],
    can_start: r.can_start === true,
    can_resume: r.can_resume === true,
    session_id: str(r.session_id),
    provider: str(r.provider),
    session_status: (IDENTITY_SESSION_STATUSES as readonly string[]).includes(r.session_status as string)
      ? (r.session_status as IdentitySessionStatus)
      : null,
    session_created_at: str(r.session_created_at),
    session_updated_at: str(r.session_updated_at),
    verified_first_name: str(r.verified_first_name),
    verified_last_name: str(r.verified_last_name),
    verified_date_of_birth: str(r.verified_date_of_birth),
    verified_issuing_country: str(r.verified_issuing_country),
    verified_document_type: str(r.verified_document_type),
    verified_at: str(r.verified_at),
    name_matches: typeof r.name_matches === 'boolean' ? r.name_matches : null,
    limit_until: str(r.limit_until),
  };
}
